import { Component, h, State, Element, Listen, forceUpdate, Prop, Method } from '@stencil/core';
import { isBreakpointEqualOrLarger, isBreakpointSmaller, setScrollLock } from '../../../utils';

@Component({
  tag: 'dc-ui-header-main-nav',
  styleUrl: 'dc-ui-header-main-nav.scss',
  shadow: true,
})
export class DcUiHeaderMainNav {
  @Element() el!: HTMLElement;

  /**
   * The maximum number of entries in the main navigation before overflow is shown.
   * @default 11
   */
  @Prop() maxEntries: number = 11;
  @Prop() overflowNextLabel: string = 'Next';
  @Prop() overflowPrevLabel: string = 'Previous';

  @State() isOpen: boolean = false;
  @State() isMobile: boolean = this.checkIfMobile();

  private burger!: HTMLDcUiHeaderBurgerElement;
  private mainNavContentWrapperInner!: HTMLElement;
  private prevButton!: HTMLDcUiButtonElement;
  private nextButton!: HTMLDcUiButtonElement;

  @State() navOverflow: boolean = false;

  private slotObserver: MutationObserver;

  @Listen('click', { target: 'window' })
  handleWindowClick(event: MouseEvent) {
    if (!this.isOpen) return;

    const path = event.composedPath();
    const excludedComponents = [
      'dc-ui-header-burger',
      'dc-ui-header-language',
      'dc-ui-header-user-menu',
    ];

    // Check if click is within any excluded component
    const isExcludedComponent = excludedComponents.some((selector) =>
      path.some(
        (element) =>
          element instanceof HTMLElement && element.tagName.toLowerCase() === selector.toLowerCase()
      )
    );

    if (!path.includes(this.el) && !isExcludedComponent) {
      this.isOpen = false;
      setScrollLock(false);
      this.burger?.dcToggleBurger();
    }
  }

  @Listen('dcHeaderBurgerClick', { target: 'window' })
  handleBurgerClick(event: CustomEvent<boolean>) {
    this.isOpen = event.detail;
    setScrollLock(event.detail);
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isOpen) {
      this.isOpen = false;
      setScrollLock(false);
      this.burger?.dcToggleBurger();
    }
  }

  componentDidLoad() {
    // Try to find burger in closest header
    const headerEl = this.el.closest('dc-ui-header');
    this.burger = headerEl?.querySelector('dc-ui-header-burger');

    // If not found, fallback to regular DOM query
    if (!this.burger) {
      this.burger = document.querySelector('dc-ui-header-burger');
    }

    window.addEventListener('resize', this.handleResize);
    this.el.addEventListener('languageButtonMoved', this.handleLanguageButtonMoved);

    if (this.maxEntries) {
      this.el.style.setProperty('--default-max-entries', this.maxEntries.toString());
    }

    // Set up the slot observer
    this.setupSlotObserver();

    // Add scroll event listener
    this.mainNavContentWrapperInner?.addEventListener('scroll', this.handleScroll);
  }

  componentDidRender() {
    this.checkForOverflow();
    this.updateButtonStates();
  }

  componentDidUpdate() {
    this.checkForOverflow();
    this.updateButtonStates();
  }

  disconnectedCallback() {
    setScrollLock(false);
    window.removeEventListener('resize', this.handleResize);
    this.el.removeEventListener('languageButtonMoved', this.handleLanguageButtonMoved);

    // Disconnect the slot observer
    if (this.slotObserver) {
      this.slotObserver.disconnect();
    }

    // Remove scroll event listener
    this.mainNavContentWrapperInner?.removeEventListener('scroll', this.handleScroll);
  }

  @Method()
  async checkForOverflow() {
    // only check for overflow on desktop
    if (isBreakpointEqualOrLarger('xl')) {
      let navItemsWidth = 0;
      const navItems = this.el.querySelectorAll(
        'dc-ui-header-main-nav-item, dc-ui-header-main-nav-link'
      );
      const maxWidth = this.el.clientWidth - 64 - 64 - 8; // 32px padding on each side and 32px button width
      const gap = 4; // 4px gap between items

      navItems.forEach((item: HTMLElement, index) => {
        const itemWidth = item.clientWidth + (index < navItems.length - 1 ? gap : 0);
        navItemsWidth += itemWidth;
      });

      this.navOverflow = navItemsWidth > maxWidth;
    } else {
      this.navOverflow = false;
    }

    this.updateButtonStates();
  }

  private handleLanguageButtonMoved = () => {
    // Handle the event, e.g., force update or any other logic
    forceUpdate(this.el);
  };

  private checkIfMobile(): boolean {
    return isBreakpointSmaller('xl');
  }

  private handleResize = () => {
    const wasMobile = this.isMobile;
    this.isMobile = this.checkIfMobile();

    // Check for overflow on resize
    this.checkForOverflow();

    // If breakpoint changed, close navigation and flyouts
    if (wasMobile !== this.isMobile) {
      // Close the mobile navigation if it's open
      if (this.isOpen) {
        this.isOpen = false;
        setScrollLock(false);
        this.burger?.dcToggleBurger();
      }

      // Close all flyouts by finding all active nav items and removing their active state
      const activeNavItems = this.el.querySelectorAll('dc-ui-header-main-nav-item');
      activeNavItems.forEach((item: HTMLElement) => {
        if (item.classList.contains('is-active')) {
          // Access the component instance to call its closeFlyout method
          const itemComponent = item as HTMLDcUiHeaderMainNavItemElement;
          if (typeof itemComponent.closeFlyout === 'function') {
            itemComponent.closeFlyout();
          }
        }
      });
    }
  };

  private scrollNav(direction: 'prev' | 'next') {
    const scrollAmount = this.mainNavContentWrapperInner.clientWidth;

    if (direction === 'next') {
      this.mainNavContentWrapperInner.scrollTo({
        left: this.mainNavContentWrapperInner.scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    } else {
      this.mainNavContentWrapperInner.scrollTo({
        left: this.mainNavContentWrapperInner.scrollLeft - scrollAmount,
        behavior: 'smooth',
      });
    }

    // Remove the setTimeout and add scroll event listeners
    if ('onscrollend' in this.mainNavContentWrapperInner) {
      // Modern browsers
      const scrollEndHandler = () => {
        this.updateButtonStates();
        this.mainNavContentWrapperInner.removeEventListener('scrollend', scrollEndHandler);
      };
      this.mainNavContentWrapperInner.addEventListener('scrollend', scrollEndHandler);
    } else {
      // Fallback for older browsers and Safari
      let scrollTimeout: number;
      const scrollHandler = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(() => {
          this.updateButtonStates();
          this.mainNavContentWrapperInner.removeEventListener('scroll', scrollHandler);
        }, 150);
      };
      (this.mainNavContentWrapperInner as HTMLElement).addEventListener('scroll', scrollHandler);
    }
  }

  @Method()
  async updateButtonStates() {
    const wrapper = this.mainNavContentWrapperInner;
    const scrollThreshold = 10; // increased threshold for better tolerance

    if (this.prevButton) {
      this.prevButton.setAttribute(
        'disabled',
        wrapper.scrollLeft <= scrollThreshold ? 'true' : 'false'
      );
    }
    if (this.nextButton) {
      const remainingScroll = wrapper.scrollWidth - (wrapper.scrollLeft + wrapper.clientWidth);
      this.nextButton.setAttribute(
        'disabled',
        remainingScroll <= scrollThreshold ? 'true' : 'false'
      );
    }
  }

  private setupSlotObserver() {
    // Create mutation observer to watch for changes in slot content
    this.slotObserver = new MutationObserver(() => {
      // Check if all nav items are hydrated
      const navItems = this.el.querySelectorAll(
        'dc-ui-header-main-nav-item, dc-ui-header-main-nav-link'
      );
      const allHydrated = Array.from(navItems).every((item) => item.classList.contains('hydrated'));

      if (allHydrated) {
        this.checkForOverflow();
        this.updateButtonStates();
      }
    });

    // Observe the host element for child changes and class changes
    this.slotObserver.observe(this.el, {
      childList: true, // Watch for changes to child elements
      subtree: true,
      attributes: true, // Watch for attribute changes
      attributeFilter: ['class'], // Only watch class attribute changes
    });
  }

  private handleScroll = () => {
    this.updateButtonStates();
  };

  render() {
    const hasMetaNavElements = this.el.querySelector('[slot="meta-nav"]') !== null;

    return (
      <nav
        class={{
          'main-nav': true,
          'is-mobile': this.isMobile,
          'is-open': this.isOpen,
        }}>
        <div class="main-nav-content">
          <div
            class={{
              'main-nav-content-wrapper': true,
              overflow: this.navOverflow,
            }}>
            {this.navOverflow && (
              <dc-ui-button
                class="overflow-nav-btn"
                ref={(el) => (this.prevButton = el)}
                variant="link"
                size="small"
                icon="dc_backward_left"
                onClick={() => this.scrollNav('prev')}
                hideLabel={true}>
                {this.overflowPrevLabel}
              </dc-ui-button>
            )}
            <div
              ref={(el) => (this.mainNavContentWrapperInner = el)}
              class={{
                'main-nav-content-wrapper-inner': true,
                overflow: this.navOverflow,
              }}>
              <slot />
            </div>

            {this.navOverflow && (
              <dc-ui-button
                class="overflow-nav-btn"
                ref={(el) => (this.nextButton = el)}
                variant="link"
                size="small"
                icon="dc_forward_right"
                onClick={() => this.scrollNav('next')}
                hideLabel={true}>
                {this.overflowNextLabel}
              </dc-ui-button>
            )}
          </div>

          {hasMetaNavElements && (
            <div class="meta-nav-wrapper">
              <slot name="meta-nav" />
            </div>
          )}
        </div>
      </nav>
    );
  }
}
